import { LitElement, html, css } from 'lit';
import './crusher-sparkline.js';

/**
 * CrusherKpi
 *
 * The dashboard staple: a metric tile with a big value, a label, a delta (up/down %), and an
 * optional sparkline trend. Composes crusher-sparkline rather than re-implementing it.
 *
 * Properties:
 *   - label     (string), the metric name ("Monthly revenue")
 *   - value     (string|number), the headline figure ("$12.4k")
 *   - delta     (number), the % change vs previous period; positive = up/green, negative = down/red
 *   - tone      (enum), sparkline + accent tone (default "brand")
 *   - data      (Array of numbers), the sparkline series
 *   - invert    (boolean), when true a negative delta reads as good (e.g. "costs down" is green)
 *
 * No events; display primitive. Token-driven.
 */
const TONES = ['brand', 'success', 'warning', 'danger', 'info', 'neutral'];

export class CrusherKpi extends LitElement {
  static properties = {
    label: { type: String },
    value: { type: String },
    delta: { type: Number, reflect: true },
    tone: { type: String, reflect: true },
    data: { type: Array, converter: { fromAttribute: (v) => { try { return v ? JSON.parse(v) : []; } catch { return []; } } } },
    invert: { type: Boolean, reflect: true },
  };

  constructor() {
    super();
    this.label = '';
    this.value = '';
    this.delta = 0;
    this.tone = 'brand';
    this.data = [];
    this.invert = false;
  }

  static styles = css`
    :host {
      display: block;
      background: var(--crusher-color-background-surface);
      border: 1px solid var(--crusher-color-border-primary);
      border-radius: var(--crusher-radius-lg, 14px);
      padding: var(--crusher-spacing-4, 1rem);
      box-shadow: var(--crusher-elevation-1);
    }
    .kpi-label {
      font-size: var(--crusher-font-size-xs);
      font-weight: var(--crusher-font-weight-semibold);
      color: var(--crusher-color-text-secondary);
      text-transform: uppercase;
      letter-spacing: 0.06em;
      margin: 0 0 0.35rem;
    }
    .kpi-value {
      font-family: var(--crusher-font-family-display);
      font-size: var(--crusher-font-size-3xl);
      font-weight: var(--crusher-font-weight-bold);
      line-height: 1.1;
      margin: 0;
      color: var(--crusher-color-text-primary);
    }
    .kpi-row {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      gap: 0.75rem;
      margin-top: 0.5rem;
    }
    .delta {
      font-family: var(--crusher-font-family-mono, monospace);
      font-size: var(--crusher-font-size-sm);
      font-weight: var(--crusher-font-weight-semibold);
      padding: 0.15rem 0.5rem;
      border-radius: 999px;
      white-space: nowrap;
    }
    .delta[data-dir='up'] { background: color-mix(in srgb, var(--crusher-color-status-success) 16%, transparent); color: var(--crusher-color-status-success); }
    .delta[data-dir='down'] { background: color-mix(in srgb, var(--crusher-color-status-failure) 16%, transparent); color: var(--crusher-color-status-failure); }
    .delta[data-dir='flat'] { background: var(--crusher-color-background-surface); color: var(--crusher-color-text-secondary); }
    /* invert: negative delta is good (costs down = green) */
    :host([invert]) .delta[data-dir='down'] { background: color-mix(in srgb, var(--crusher-color-status-success) 16%, transparent); color: var(--crusher-color-status-success); }
    :host([invert]) .delta[data-dir='up'] { background: color-mix(in srgb, var(--crusher-color-status-failure) 16%, transparent); color: var(--crusher-color-status-failure); }
  `;

  render() {
    const d = this.delta || 0;
    const dir = d > 0.5 ? 'up' : d < -0.5 ? 'down' : 'flat';
    const arrow = dir === 'up' ? '▲' : dir === 'down' ? '▼' : '—';
    return html`
      <p class="kpi-label">${this.label}</p>
      <p class="kpi-value">${this.value}</p>
      <div class="kpi-row">
        <span class="delta" data-dir=${dir}>${arrow} ${Math.abs(d).toFixed(1)}%</span>
        ${this.data && this.data.length
          ? html`<crusher-sparkline
              .data=${this.data}
              tone=${this.tone}
              height="32"
              width="120"
              show-area
            ></crusher-sparkline>`
          : null}
      </div>
    `;
  }
}

customElements.define('crusher-kpi', CrusherKpi);
export default CrusherKpi;
